import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 px-3 rounded-md font-heading font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-[var(--button-primary-background)] text-[var(--button-primary-color)] shadow-[var(--shadow-light-only)] [border:var(--shadow-border-light-only)] hover:bg-[var(--button-primary-hover)] dark:[background:var(--glass-background)] dark:[background-blend-mode:var(--glass-blend-mode)] dark:[-webkit-backdrop-filter:var(--glass-blur)] dark:[backdrop-filter:var(--glass-blur)] dark:[border:var(--glass-border)] dark:text-[var(--text-secondary)] dark:shadow-none',
        submit:
          'border border-[var(--color-neutral-900)] bg-[var(--color-neutral-900)] text-[var(--color-neutral-25)] hover:border-[var(--color-neutral-800)] hover:bg-[var(--color-neutral-800)] dark:[background:var(--glass-background)] dark:[background-blend-mode:var(--glass-blend-mode)] dark:[-webkit-backdrop-filter:var(--glass-blur)] dark:[backdrop-filter:var(--glass-blur)] dark:[border:var(--glass-border)] dark:text-[var(--text-secondary)] dark:hover:bg-[var(--button-primary-hover)]',
        glass:
          '[background:var(--button-glass-background)] [background-blend-mode:var(--button-glass-blend-mode)] [-webkit-backdrop-filter:var(--button-glass-blur)] [backdrop-filter:var(--button-glass-blur)] [border:var(--button-glass-border)] text-[var(--button-glass-color)] shadow-none hover:[background:var(--button-glass-hover)]',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline:
          'border border-border-subtle text-fg-primary shadow-none hover:bg-surface-muted hover:text-fg-primary dark:border-border-subtle dark:bg-transparent dark:hover:bg-neutral-0-30',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'bg-transparent text-fg-primary !shadow-none hover:bg-accent hover:text-accent-foreground hover:!shadow-none focus-visible:!shadow-none active:!shadow-none dark:hover:bg-neutral-0-10',
        link: 'bg-transparent text-fg-primary underline-offset-4 hover:underline',
        'brand-gradient':
          'bg-[linear-gradient(90.28deg,#5652ad_6.34%,#c37c86_105%)] text-[var(--color-neutral-25)] [a&]:hover:bg-[linear-gradient(90.28deg,#5652ad_6.34%,#c37c86_105%)]',
        oauth: 'bg-neutral-900 text-neutral-25',
        // A solid fill in the primary text color with inverse ink, held through
        // hover: the chat composer's send/stop circle. Without the pinned hover
        // ink, ghost's `hover:text-accent-foreground` paints the icon the same
        // color as the fill and it disappears.
        inverse:
          'bg-fg-primary text-fg-inverse hover:bg-fg-primary hover:text-fg-inverse dark:hover:bg-fg-primary dark:hover:text-fg-inverse',
        tight:
          'bg-transparent text-fg-primary !shadow-none hover:text-accent-foreground hover:!shadow-none focus-visible:!shadow-none active:!shadow-none !p-0 opacity-80 hover:opacity-100',
      },
      size: {
        xs: 'h-6 gap-1 px-2 text-[.6rem] rounded-xs',
        sm: 'h-8 gap-1.5 px-3 text-xs rounded-sm',
        default: 'h-10 text-sm',
        lg: 'h-12 px-6',
        icon: 'size-9',
        'icon-xs': 'size-6 rounded-xs',
        'icon-sm': 'size-8 rounded-sm',
        'icon-lg': 'size-10',
        // A rounded, compact action in a row of peers (the lecture preview's
        // utility row, ORA-824).
        pill: 'h-9 gap-1.5 px-3.5 text-sm rounded-full',
      },
      context: {
        default: '',
        modal:
          '!border !border-solid !border-[var(--color-neutral-0-30)] !bg-[var(--color-neutral-0-10)] text-[var(--color-neutral-25)] hover:!border-[var(--color-neutral-0-30)] hover:!bg-[var(--color-neutral-0-30)] hover:text-[var(--color-neutral-25)] dark:!border-[var(--color-neutral-0-30)] dark:bg-[var(--color-neutral-0-10)] dark:text-[var(--color-neutral-25)] dark:hover:!border-[var(--color-neutral-0-30)] dark:hover:bg-[var(--color-neutral-0-30)] dark:hover:text-[var(--color-neutral-25)] ![-webkit-backdrop-filter:none] ![background-image:none] dark:![-webkit-backdrop-filter:none] dark:![background-image:none]',
        menu: 'w-full justify-start px-3 py-1.5 text-neutral-25 hover:bg-neutral-0-10 hover:text-neutral-25 focus-visible:bg-neutral-0-10 focus-visible:text-neutral-25',
      },
      pressed: {
        true: 'bg-neutral-0-30',
        false: '',
      },
      // A circle (icon sizes) or a pill; after `size`, so it wins the radius.
      shape: {
        default: '',
        round: 'rounded-full',
      },
    },
    compoundVariants: [
      {
        variant: 'ghost',
        pressed: true,
        className:
          'bg-surface-highlight text-fg-primary dark:bg-neutral-0-10 dark:text-fg-primary',
      },
      {
        context: 'menu',
        pressed: true,
        className: 'bg-neutral-0-10 text-neutral-25',
      },
      {
        variant: 'outline',
        context: 'modal',
        className:
          '!text-neutral-0 !bg-transparent hover:!bg-[var(--color-neutral-0-10)] dark:!bg-transparent dark:hover:!bg-[var(--color-neutral-0-10)]',
      },
      {
        variant: 'ghost',
        context: 'modal',
        className:
          '!text-neutral-0 !bg-transparent hover:!bg-[var(--color-neutral-0-10)] !border-none',
      },
      {
        variant: 'destructive',
        context: 'modal',
        className:
          '!border-destructive !bg-destructive !text-neutral-25 hover:!border-destructive/90 hover:!bg-destructive/90 hover:!text-neutral-25 dark:!border-destructive dark:!bg-destructive dark:!text-neutral-25 dark:hover:!border-destructive/90 dark:hover:!bg-destructive/90 dark:hover:!text-neutral-25',
      },
      {
        variant: 'destructive',
        context: 'modal',
        className:
          '!border-destructive !bg-destructive !text-neutral-25 hover:!border-destructive/90 hover:!bg-destructive/90 hover:!text-neutral-25 dark:!border-destructive dark:!bg-destructive dark:!text-neutral-25 dark:hover:!border-destructive/90 dark:hover:!bg-destructive/90 dark:hover:!text-neutral-25',
      },
      {
        variant: 'tight',
        size: 'icon',
        className: 'w-auto h-auto p-0',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      context: 'default',
      pressed: false,
      shape: 'default',
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      context = 'default',
      pressed,
      shape = 'default',
      'aria-pressed': ariaPressedProp,
      asChild = false,
      type,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot.Root : 'button';
    const resolvedPressed = pressed ?? undefined;
    const resolvedAriaPressed = ariaPressedProp ?? resolvedPressed;
    const resolvedType = asChild ? undefined : (type ?? 'button');

    return (
      <Comp
        ref={ref}
        type={resolvedType}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        data-context={context}
        data-pressed={resolvedPressed}
        data-shape={shape}
        aria-pressed={resolvedAriaPressed}
        className={cn(
          buttonVariants({ variant, size, context, pressed, shape, className })
        )}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
